import SearchOffOutlined from "@mui/icons-material/SearchOffOutlined";
import { Box, Pagination, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { ReassistSearchField } from "@/features/reassistRequests/components/ReassistSearchField";
import { LoadFailed } from "@/features/reassistRequests/components/StateBlocks";
import { TEXT } from "@/features/reassistRequests/utils/tokens";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MIN_TAP_TARGET } from "@/styles/responsive";
import { SALE_PAGE_SIZE, useSaleDocuments } from "../api/useReassistSale";
import { SaleDocumentCard } from "./SaleDocumentCard";
import { SaleEmptyState } from "./SaleEmptyState";
import { SaleListSkeleton } from "./SaleListSkeleton";

interface Props {
  onSelect: (id: number) => void;
}

/** 検索語の入力が止まってから問い合わせるまでの待ち時間（ミリ秒）。 */
const DEBOUNCE_MS = 300;
const SEARCH_LABEL = "マンション名、住所、地番、家屋番号で検索";
const SEARCH_INPUT_ID = "sale-search-input";

/** 一覧検索。マンション名・住所・地番・家屋番号の部分一致で探す。 */
export const SaleSearchList: React.FC<Props> = ({ onSelect }) => {
  const isMobile = useIsMobile();
  const [input, setInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setKeyword(input.trim());
      setPage(1);
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [input]);

  const { data, isLoading, error, mutate } = useSaleDocuments(
    keyword,
    (page - 1) * SALE_PAGE_SIZE
  );
  const pageCount =
    data === undefined ? 0 : Math.ceil(data.count / SALE_PAGE_SIZE);

  return (
    <Box>
      {/* /reassist のツールバーと同じ型（1行。左=検索欄、右=件数） */}
      <Stack
        direction="row"
        gap={2}
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Box sx={{ flex: "1 1 auto", minWidth: 0, maxWidth: 480 }}>
          <ReassistSearchField
            id={SEARCH_INPUT_ID}
            value={input}
            onChange={setInput}
            label={SEARCH_LABEL}
            onClear={() => {
              setInput("");
            }}
          />
        </Box>
        <Typography
          aria-live="polite"
          sx={{
            ...TEXT.meta,
            color: "text.secondary",
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {data !== undefined && `${data.count.toLocaleString("ja-JP")}件`}
        </Typography>
      </Stack>
      <Box sx={{ mt: 2 }}>
        {error !== undefined && (
          <LoadFailed
            message="資料の一覧を読み込めませんでした。少し待ってから、もう一度お試しください。"
            onRetry={() => {
              mutate().catch(() => undefined);
            }}
          />
        )}
        {error === undefined && isLoading && data === undefined && (
          <SaleListSkeleton />
        )}
        {data !== undefined &&
          (data.results.length === 0 ? (
            <SaleEmptyState
              icon={SearchOffOutlined}
              title={
                keyword === ""
                  ? "購入できる資料はありません"
                  : "条件に合う資料はありません"
              }
              description={
                keyword === ""
                  ? undefined
                  : "検索語を変えて、もう一度お試しください。"
              }
            />
          ) : (
            <Stack
              component="ul"
              gap={1.5}
              sx={{ p: 0, m: 0 }}
              aria-label="販売中の資料"
              aria-busy={isLoading}
            >
              {data.results.map((document) => (
                <SaleDocumentCard
                  key={document.id}
                  document={document}
                  onSelect={onSelect}
                />
              ))}
            </Stack>
          ))}
      </Box>
      {pageCount > 1 && (
        <Stack sx={{ mt: 3, alignItems: "center" }} gap={0.5}>
          <Pagination
            count={pageCount}
            page={page}
            shape="rounded"
            color="primary"
            siblingCount={isMobile ? 0 : 1}
            boundaryCount={isMobile ? 0 : 1}
            onChange={(_, value) => {
              setPage(value);
            }}
            sx={
              isMobile
                ? {
                    "& .MuiPaginationItem-root": {
                      minWidth: MIN_TAP_TARGET,
                      height: MIN_TAP_TARGET,
                    },
                  }
                : undefined
            }
          />
          {isMobile && (
            <Typography
              sx={{
                ...TEXT.meta,
                color: "text.secondary",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {page} / {pageCount}ページ
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};
